import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn_components/ui/popover';
import { cn } from '@/lib/tailwind-utils';
import { Edge } from '@xyflow/react';
import React, { useCallback } from 'react';
import { Label } from '@/shadcn_components/ui/label';
import { ColorPicker } from '@/components/basic/ColorPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shadcn_components/ui/select';
import { Separator } from '@/shadcn_components/ui/separator';
import useWorkflowStore from '../store';

const LINE_TYPES = [
  { id: 'customSmooth', label: 'Mượt' },
  { id: 'customStraight', label: 'Thẳng' },
  { id: 'customTwoPoint', label: 'Gấp khúc' },
  { id: 'customStep', label: 'Bậc thang' },
];

const LINE_STYLES = [
  { id: 'solid', label: 'Nét liền', dash: undefined },
  { id: 'dotted', label: 'Nét đứt ngắn', dash: '5,5' },
  { id: 'dashed', label: 'Nét đứt vừa', dash: '10,10' },
  { id: 'long-dash', label: 'Nét đứt dài', dash: '20,10' },
  { id: 'dash-dot', label: 'Gạch chấm', dash: '10,5,3,5' },
  { id: 'long-dash-dot', label: 'Gạch dài chấm', dash: '5,5,5,5,30,5' },
];

const scaleDashArray = (dash: string | undefined, strokeWidth: number) => {
  if (!dash) return undefined;
  return dash
    .split(',')
    .map((num) => parseFloat(num) * (strokeWidth / 2))
    .join(',');
};

type EdgeStyleSetProps = {
  selectedEdges: Edge[];
  onEdgeStyleChange: (style: Partial<Edge>) => void;
};

export const EdgeStyleSetting = (props: EdgeStyleSetProps) => {
  const { selectedEdges, onEdgeStyleChange } = props;
  const [color, setColor] = React.useState(selectedEdges[0]?.style?.stroke || '#000000');
  const strokeWidth = parseFloat(selectedEdges[0]?.style?.strokeWidth?.toString() || '1');

  return (
    <div className="space-y-4">
      {/* Kiểu nối */}
      <div>
        <Label className="text-sm font-medium">Kiểu nối</Label>
        <div className="grid grid-cols-2 gap-2">
          {LINE_TYPES.map((type) => (
            <button
              key={type.id}
              className={cn(
                'px-3 py-1 text-sm border rounded',
                selectedEdges.every((e) => e.type === type.id)
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted'
              )}
              onClick={() => onEdgeStyleChange({ type: type.id })}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Kiểu nét */}
      <Separator />
      <div>
        <Select
          defaultValue={
            LINE_STYLES.find((s) =>
              selectedEdges.every(
                (e) =>
                  e.style?.strokeDasharray === scaleDashArray(s.dash, strokeWidth) ||
                  (!e.style?.strokeDasharray && s.dash === undefined)
              )
            )?.id
          }
          onValueChange={(value) => {
            const selectedStyle = LINE_STYLES.find((style) => style.id === value);
            if (selectedStyle) {
              onEdgeStyleChange({
                style: {
                  strokeDasharray: scaleDashArray(selectedStyle.dash, strokeWidth),
                },
              });
            }
          }}
        >
          <SelectTrigger className="w-full mt-2">
            <SelectValue placeholder="Chọn kiểu nét" />
          </SelectTrigger>
          <SelectContent>
            {LINE_STYLES.map((style) => (
              <SelectItem key={style.id} value={style.id}>
                <svg width="100%" height="20" viewBox="0 0 150 20" className="w-full">
                  <line
                    x1="0"
                    y1="10"
                    x2="150"
                    y2="10"
                    stroke="black"
                    strokeWidth={strokeWidth}
                    strokeDasharray={scaleDashArray(style.dash, strokeWidth)}
                  />
                </svg>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Độ dày đường */}
      <div>
        <Label className="text-sm font-medium">Độ dày</Label>
        <Select
          defaultValue={String(strokeWidth)}
          onValueChange={(strokeWidthStr) => {
            const newStrokeWidth = parseFloat(strokeWidthStr);
            const currentDash = LINE_STYLES.find((s) =>
              selectedEdges.every(
                (e) =>
                  e.style?.strokeDasharray ===
                  scaleDashArray(s.dash, parseFloat(e.style?.strokeWidth?.toString() || '1'))
              )
            )?.dash;

            onEdgeStyleChange({
              style: {
                strokeWidth: newStrokeWidth,
                strokeDasharray: scaleDashArray(currentDash, newStrokeWidth),
              },
            });
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Độ dày đường" />
          </SelectTrigger>
          <SelectContent>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
              <SelectItem key={value} value={String(value)}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Màu sắc */}
      <div>
        <ColorPicker
          color={color}
          onChange={(color) => {
            setColor(color);
            onEdgeStyleChange({ style: { stroke: color } });
          }}
        />
      </div>
    </div>
  );
};

export function EdgeStyleSetButton(props: { children: React.ReactNode }) {
  const { children } = props;
  const { selectedEdges, setSelectedEdges, edges, setEdges, takeSnapshot } = useWorkflowStore();

  // Xử lý thay đổi style của cạnh
  const handleEdgeStyleChange = useCallback(
    (style: Partial<Edge>) => {
      if (selectedEdges.length === 0) return;

      takeSnapshot();

      const newEdges = edges.map((edge) => {
        if (selectedEdges.find((e) => e.id === edge.id)) {
          return {
            ...edge,
            ...style,
            style: { ...edge.style, ...style.style },
          };
        }
        return edge;
      });

      setEdges(newEdges);
      setSelectedEdges(newEdges.filter((e) => selectedEdges.some((se) => se.id === e.id)));
    },
    [selectedEdges, setSelectedEdges, edges, setEdges, takeSnapshot]
  );

  if (selectedEdges.length === 0) {
    return children;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-64">
        <EdgeStyleSetting selectedEdges={selectedEdges} onEdgeStyleChange={handleEdgeStyleChange} />
      </PopoverContent>
    </Popover>
  );
}
